/**
 * @fileoverview Confirm My Page payment mutation hook.
 * @description A Super Admin confirms the bank's payment (POST /sales/targets/{targetId}/payment/confirm),
 * completing the target and making the prospect Live. Sends the `tg1.` version the dialog showed
 * as If-Match and needs a recent MFA sign-in.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { SALES_QUERY_KEY } from '@/features/sales/constants/prospects-query-keys.constants'
import { confirmMyPagePayment } from '@/features/sales/services/my-page-targets.service'

/**
 * @description Sends the request, then refreshes every sales query so the table, KPIs and
 * details show the target's new state.
 * @param {() => void} [onSuccess] - Called after a success, even when the dialog has unmounted
 * since: it is an option of the mutation, not of a `mutate` call.
 * @returns {ReturnType<typeof useMutation>} The mutation (mutate with the target id and the version shown).
 */
export function useConfirmMyPagePayment(onSuccess?: () => void) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ targetId, version }: { targetId: string; version: string }) =>
      confirmMyPagePayment(targetId, version),
    /**
     * @description Refreshes every sales query, then reports the success.
     */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SALES_QUERY_KEY })
      onSuccess?.()
    },
  })
}
